import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../ui/card';
import { ArrowLeft, ArrowRight, Mail, Check } from 'lucide-react';
import {
  INTENT_OPTIONS,
  UNIVERSAL_QUESTIONS,
  ARCHETYPES,
  getBranchQuestions,
  calculateResult,
  getPersonalizedHeadline,
} from '../../lib/connection-readiness-data';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] },
});

// --- LANDING ---
const Landing = ({ onStart }) => (
  <section className="min-h-[80vh] px-6 pt-28 sm:pt-32 pb-16">
    <div className="max-w-lg mx-auto text-center">
      <motion.span
        {...fade(0)}
        className="inline-block text-xs font-medium tracking-[0.2em] uppercase text-[hsl(var(--teal))] mb-6"
      >
        Guided Self-Discovery
      </motion.span>
      <motion.h1
        {...fade(0.06)}
        className="text-3xl sm:text-4xl lg:text-5xl font-light text-foreground tracking-tight leading-[1.15] mb-5"
      >
        Not sure if you're actually ready?{' '}
        <span className="italic text-[hsl(var(--gold))]">Start here.</span>
      </motion.h1>
      <motion.p
        {...fade(0.12)}
        className="text-base sm:text-lg text-muted-foreground font-light max-w-md mx-auto mb-3"
      >
        Wanting connection and being ready for it aren't always the same thing.
      </motion.p>
      <motion.p
        {...fade(0.16)}
        className="text-base sm:text-lg text-muted-foreground font-light max-w-md mx-auto mb-8"
      >
        Connection Readiness helps you understand whether your expectations, energy, and availability match what you say you want.
      </motion.p>
      <motion.div {...fade(0.22)}>
        <button
          onClick={onStart}
          className="rounded-full px-8 py-6 text-base font-medium bg-[hsl(var(--gold))] text-white hover:bg-[hsl(var(--gold)/0.9)] transition-transform hover:scale-[0.98] active:scale-[0.96]"
          data-testid="readiness-start-btn"
        >
          Check Connection Readiness
        </button>
        <p className="text-xs text-muted-foreground/60 mt-4">Takes about 3–5 minutes</p>
      </motion.div>
    </div>
  </section>
);

// --- INTENT SELECTION (multi-select) ---
const IntentSelection = ({ selected, onToggle, onContinue, onBack }) => (
  <section className="min-h-[80vh] px-6 pt-28 sm:pt-32 pb-16">
    <div className="max-w-lg mx-auto w-full">
      <motion.span
        {...fade(0)}
        className="inline-block text-xs font-medium tracking-[0.2em] uppercase text-[hsl(var(--teal))] mb-4"
      >
        Step 1 of 3
      </motion.span>
      <motion.h2
        {...fade(0.04)}
        className="text-2xl sm:text-3xl font-light text-foreground tracking-tight leading-snug mb-3"
      >
        Readiness depends on what you're actually looking for.
      </motion.h2>
      <motion.p
        {...fade(0.10)}
        className="text-sm sm:text-base text-muted-foreground font-light leading-relaxed mb-3"
      >
        Different kinds of connection ask different things from us.
      </motion.p>
      <motion.p
        {...fade(0.14)}
        className="text-sm sm:text-base text-foreground/80 font-light leading-relaxed mb-8"
      >
        Tell us what you're realistically open to right now. Select all that apply.
      </motion.p>

      <motion.div {...fade(0.18)} className="space-y-3 mb-8">
        {INTENT_OPTIONS.map((opt, i) => {
          const isSelected = selected.includes(opt.id);
          return (
            <Card
              key={opt.id}
              onClick={() => onToggle(opt.id)}
              className={`cursor-pointer transition-all border ${
                isSelected
                  ? 'border-[hsl(var(--teal))] bg-[hsl(var(--teal)/0.05)]'
                  : 'border-border hover:border-[hsl(var(--teal)/0.3)]'
              }`}
              data-testid={`readiness-intent-${opt.id}`}
            >
              <CardContent className="py-4 px-5">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-4 h-4 rounded-[5px] border-2 shrink-0 flex items-center justify-center transition-colors ${
                      isSelected
                        ? 'border-[hsl(var(--teal))] bg-[hsl(var(--teal))]'
                        : 'border-muted-foreground/30'
                    }`}
                  >
                    {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                  </div>
                  <span className="text-sm text-foreground leading-relaxed">{opt.label}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </motion.div>

      <div className="flex justify-between items-center">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          data-testid="readiness-intent-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={onContinue}
          disabled={selected.length === 0}
          className={`flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-all ${
            selected.length > 0
              ? 'bg-[hsl(var(--gold))] text-white hover:bg-[hsl(var(--gold)/0.9)]'
              : 'bg-muted text-muted-foreground cursor-not-allowed'
          }`}
          data-testid="readiness-intent-continue-btn"
        >
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  </section>
);

// --- SINGLE QUESTION (universal or branch) ---
const Question = ({
  question, questionIndex, total, answer, onAnswer, onNext, onBack, isLast,
}) => {
  const progress = ((questionIndex + 1) / total) * 100;
  const canNext = answer !== null && answer !== undefined;

  return (
    <section className="min-h-[80vh] px-6 pt-28 sm:pt-32 pb-16">
      <div className="max-w-lg mx-auto w-full">
        <motion.div {...fade(0)} className="mb-10">
          <div className="flex justify-between text-xs text-muted-foreground mb-2">
            <span>Question {questionIndex + 1} of {total}</span>
          </div>
          <div className="h-1 bg-muted rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-[hsl(var(--gold))] rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
        </motion.div>

        <motion.h2
          key={question.id + '-prompt'}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-xl sm:text-2xl font-light text-foreground leading-snug mb-8"
        >
          {question.prompt}
        </motion.h2>

        <AnimatePresence mode="wait">
          <motion.div
            key={question.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {question.type === 'choice' ? (
              <div className="space-y-3">
                {question.options.map((option, i) => {
                  const isSelected = answer === i;
                  return (
                    <Card
                      key={i}
                      onClick={() => onAnswer(i)}
                      className={`cursor-pointer transition-all border ${
                        isSelected
                          ? 'border-[hsl(var(--teal))] bg-[hsl(var(--teal)/0.05)]'
                          : 'border-border hover:border-[hsl(var(--teal)/0.3)]'
                      }`}
                      data-testid={`readiness-option-${i}`}
                    >
                      <CardContent className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors ${
                              isSelected ? 'border-[hsl(var(--teal))] bg-[hsl(var(--teal))]' : 'border-muted-foreground/30'
                            }`}
                          >
                            {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                          </div>
                          <span className="text-sm text-foreground leading-relaxed">{option.label}</span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="pt-4 pb-2">
                <div className="flex justify-between text-xs text-muted-foreground mb-6">
                  <span>{question.labels[0]}</span>
                  <span>{question.labels[1]}</span>
                </div>
                <div className="flex justify-between gap-2">
                  {Array.from({ length: question.max - question.min + 1 }, (_, i) => {
                    const val = question.min + i;
                    const isSelected = answer === val;
                    return (
                      <button
                        key={val}
                        onClick={() => onAnswer(val)}
                        className={`flex-1 h-12 rounded-lg text-sm font-medium transition-all ${
                          isSelected ? 'bg-[hsl(var(--teal))] text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                        data-testid={`readiness-slider-${val}`}
                      >
                        {val}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-between items-center mt-10">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="readiness-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button
            onClick={onNext}
            disabled={!canNext}
            className={`flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-all ${
              canNext ? 'bg-[hsl(var(--gold))] text-white hover:bg-[hsl(var(--gold)/0.9)]' : 'bg-muted text-muted-foreground cursor-not-allowed'
            }`}
            data-testid="readiness-next-btn"
          >
            {isLast ? 'See Results' : 'Next'} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

// --- RESULTS ---
const Results = ({
  result, archetypeKey, intents, universalAnswers, branchQuestionIds, branchAnswers, scores,
  onTakeSurvey, onKeepExploring,
}) => {
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [sending, setSending] = useState(false);

  const personalized = getPersonalizedHeadline(archetypeKey, intents);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || sending) return;
    setSending(true);
    try {
      await fetch(`${API_URL}/api/emails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'connection-readiness' }),
      });
      setEmailSent(true);
    } catch {
      // silent
    }
    setSending(false);
  };

  useEffect(() => {
    const storeResult = async () => {
      try {
        await fetch(`${API_URL}/api/readiness-results`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            answers: universalAnswers,
            scores,
            archetype: archetypeKey,
            intents,
            branch_answers: branchAnswers,
            branch_question_ids: branchQuestionIds,
          }),
        });
      } catch {
        // silent
      }
    };
    storeResult();
  }, [universalAnswers, scores, archetypeKey, intents, branchAnswers, branchQuestionIds]);

  return (
    <section className="min-h-[80vh] px-6 pt-28 sm:pt-32 pb-20">
      <div className="max-w-lg mx-auto w-full">
        <motion.span
          {...fade(0)}
          className="inline-block text-xs font-medium tracking-[0.2em] uppercase text-[hsl(var(--teal))] mb-4"
        >
          Where You Seem to Be Right Now
        </motion.span>

        <motion.h1
          {...fade(0.06)}
          className="text-3xl sm:text-4xl font-light text-foreground tracking-tight mb-5"
          data-testid="readiness-result-label"
        >
          {result.label}
        </motion.h1>

        <motion.p
          {...fade(0.10)}
          className="text-base sm:text-lg text-foreground/85 leading-relaxed mb-5"
          data-testid="readiness-result-personalized"
        >
          {personalized}
        </motion.p>

        <motion.p
          {...fade(0.14)}
          className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-8"
          data-testid="readiness-result-narrative"
        >
          {result.narrative}
        </motion.p>

        <motion.div {...fade(0.18)} className="mb-12">
          <div className="flex flex-wrap gap-2">
            {result.bullets.map((bullet, i) => (
              <span
                key={i}
                className="px-3 py-1.5 rounded-full text-xs font-medium bg-[hsl(var(--teal)/0.08)] text-[hsl(var(--teal))]"
                data-testid={`readiness-bullet-${i}`}
              >
                {bullet}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Reflection Bridge */}
        <motion.div
          {...fade(0.22)}
          className="border-t border-border/50 pt-10 mb-10"
          data-testid="readiness-reflection-bridge"
        >
          <span className="inline-block text-xs font-medium tracking-[0.18em] uppercase text-[hsl(var(--gold))] mb-3">
            Before you do anything else
          </span>
          <h3 className="text-lg font-light text-foreground mb-3 leading-snug">
            What we're showing you is a perspective, not a verdict.
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed mb-2">
            If this feels accurate, use it as guidance when you tell HAEVN what you're looking for.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            If it doesn't, that's useful too. You may still have a few things worth sorting through.
          </p>
        </motion.div>

        {/* CTAs */}
        <motion.div {...fade(0.28)} className="mb-10">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onTakeSurvey}
              className="flex-1 rounded-full px-6 py-4 text-sm font-medium bg-[hsl(var(--gold))] text-white hover:bg-[hsl(var(--gold)/0.9)] transition-all"
              data-testid="readiness-cta-survey"
            >
              Tell HAEVN What You're Looking For
            </button>
            <button
              onClick={onKeepExploring}
              className="flex-1 rounded-full px-6 py-4 text-sm font-medium border border-border text-foreground hover:bg-secondary transition-all"
              data-testid="readiness-cta-explore"
            >
              Keep Exploring
            </button>
          </div>
          <p className="mt-4 text-xs text-muted-foreground/70 italic">
            Takes about 15 minutes. Then HAEVN takes it from there.
          </p>
        </motion.div>

        {/* Email Capture */}
        <motion.div {...fade(0.32)}>
          {emailSent ? (
            <div className="flex items-center gap-2 text-sm text-[hsl(var(--teal))]">
              <Check className="w-4 h-4" />
              <span>We'll send a copy to your inbox.</span>
            </div>
          ) : (
            <form onSubmit={handleEmailSubmit} className="flex gap-3">
              <div className="flex-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/50" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Want a copy of your results?"
                  className="w-full pl-10 pr-4 py-3 rounded-full bg-muted border border-border text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-[hsl(var(--teal)/0.5)]"
                  data-testid="readiness-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={!email || sending}
                className="rounded-full px-5 py-3 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-all"
                data-testid="readiness-email-submit"
              >
                Send
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

// --- MAIN ---
export const ConnectionReadiness = ({ onBackToHub, onGoToHAEVN }) => {
  const [step, setStep] = useState('landing'); // 'landing' | 'intent' | 'questions' | 'results'
  const [intents, setIntents] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [universalAnswers, setUniversalAnswers] = useState(Array(UNIVERSAL_QUESTIONS.length).fill(null));
  const [branchAnswers, setBranchAnswers] = useState([]);
  const [result, setResult] = useState(null);

  // Branch questions are derived from intent selection
  const branchQuestions = useMemo(() => getBranchQuestions(intents), [intents]);
  const allQuestions = useMemo(() => [...UNIVERSAL_QUESTIONS, ...branchQuestions], [branchQuestions]);
  const totalQuestions = allQuestions.length;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step, questionIndex]);

  const handleStart = useCallback(() => {
    setStep('intent');
    setIntents([]);
    setQuestionIndex(0);
    setUniversalAnswers(Array(UNIVERSAL_QUESTIONS.length).fill(null));
    setBranchAnswers([]);
  }, []);

  const handleToggleIntent = useCallback((id) => {
    setIntents((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const handleIntentContinue = useCallback(() => {
    // Initialize branch answers array sized to branch questions
    const branches = getBranchQuestions(intents);
    setBranchAnswers(Array(branches.length).fill(null));
    setQuestionIndex(0);
    setStep('questions');
  }, [intents]);

  const setAnswerAtIndex = useCallback((value) => {
    const u = UNIVERSAL_QUESTIONS.length;
    if (questionIndex < u) {
      setUniversalAnswers((prev) => {
        const updated = [...prev];
        updated[questionIndex] = value;
        return updated;
      });
    } else {
      const bIdx = questionIndex - u;
      setBranchAnswers((prev) => {
        const updated = [...prev];
        updated[bIdx] = value;
        return updated;
      });
    }
  }, [questionIndex]);

  const handleNext = useCallback(() => {
    if (questionIndex < totalQuestions - 1) {
      setQuestionIndex((p) => p + 1);
    } else {
      const res = calculateResult({
        universalAnswers,
        branchQuestions,
        branchAnswers,
      });
      setResult(res);
      setStep('results');
    }
  }, [questionIndex, totalQuestions, universalAnswers, branchQuestions, branchAnswers]);

  const handleBack = useCallback(() => {
    if (questionIndex > 0) {
      setQuestionIndex((p) => p - 1);
    } else {
      setStep('intent');
    }
  }, [questionIndex]);

  if (step === 'landing') {
    return <Landing onStart={handleStart} />;
  }

  if (step === 'intent') {
    return (
      <IntentSelection
        selected={intents}
        onToggle={handleToggleIntent}
        onContinue={handleIntentContinue}
        onBack={() => setStep('landing')}
      />
    );
  }

  if (step === 'questions') {
    const u = UNIVERSAL_QUESTIONS.length;
    const currentQuestion = allQuestions[questionIndex];
    const currentAnswer =
      questionIndex < u ? universalAnswers[questionIndex] : branchAnswers[questionIndex - u];
    return (
      <Question
        question={currentQuestion}
        questionIndex={questionIndex}
        total={totalQuestions}
        answer={currentAnswer}
        onAnswer={setAnswerAtIndex}
        onNext={handleNext}
        onBack={handleBack}
        isLast={questionIndex === totalQuestions - 1}
      />
    );
  }

  if (step === 'results' && result) {
    return (
      <Results
        result={result.result}
        archetypeKey={result.archetype}
        intents={intents}
        universalAnswers={universalAnswers}
        branchQuestionIds={branchQuestions.map((q) => q.id)}
        branchAnswers={branchAnswers}
        scores={result.scores}
        onTakeSurvey={() => onGoToHAEVN && onGoToHAEVN()}
        onKeepExploring={() => onBackToHub && onBackToHub()}
      />
    );
  }

  return null;
};
